import dayjs from "dayjs";
import type { PaymentDetailData } from "../types/consultationsBilling.types";

export const APPOINTMENT_OVERDUE_TOLERANCE_MINUTES = 60;

export function isPastScheduledTimeWithTolerance(start?: string | null) {
  if (!start) return false;
  return dayjs(start)
    .add(APPOINTMENT_OVERDUE_TOLERANCE_MINUTES, "minute")
    .isBefore(dayjs());
}

export function isActionablePendingAppointment(appointment: fhir5.Appointment) {
  if (appointment.status !== "pending") return false;
  if (!appointment.start) return true;
  return !isPastScheduledTimeWithTolerance(appointment.start);
}

export function isOverdueBookedAppointment(appointment: fhir5.Appointment) {
  if (appointment.status !== "booked" || !appointment.start) return false;
  return isPastScheduledTimeWithTolerance(appointment.start);
}

export function isExpiredPendingAppointment(appointment: fhir5.Appointment) {
  if (appointment.status !== "pending") return false;
  if (!appointment.start) return false;
  return isPastScheduledTimeWithTolerance(appointment.start);
}

export function isStalePendingPayment(payment: PaymentDetailData) {
  if (payment.payment.status !== "active") return false;

  const appointmentStatus = payment.appointment?.status;
  if (appointmentStatus === "fulfilled" || appointmentStatus === "cancelled") {
    return false;
  }

  return isPastScheduledTimeWithTolerance(payment.slot?.start);
}

export type AppointmentSituationKind =
  | "confirmed"
  | "awaiting_payment"
  | "needs_reschedule"
  | "fulfilled"
  | "cancelled_by_patient"
  | "payment_not_completed";

export type SituationIconKey =
  | "eventAvailable"
  | "schedule"
  | "eventBusy"
  | "checkCircle"
  | "cancel"
  | "block"
  | "attachMoney";

export type SituationTone = "success" | "warning" | "error" | "info" | "neutral";

export interface SituationDisplayMeta {
  label: string;
  shortLabel: string;
  iconKey: SituationIconKey;
  tone: SituationTone;
  emphasized?: boolean;
}

export const APPOINTMENT_SITUATION_META: Record<
  AppointmentSituationKind,
  SituationDisplayMeta
> = {
  confirmed: {
    label: "Confirmada (paga ou gratuita)",
    shortLabel: "Confirmada",
    iconKey: "eventAvailable",
    tone: "success",
  },
  awaiting_payment: {
    label: "Aguardando pagamento",
    shortLabel: "Aguardando",
    iconKey: "schedule",
    tone: "warning",
    emphasized: true,
  },
  needs_reschedule: {
    label: "Precisa reagendar — consulta não ocorreu",
    shortLabel: "Reagendar",
    iconKey: "eventBusy",
    tone: "error",
    emphasized: true,
  },
  fulfilled: {
    label: "Consulta realizada",
    shortLabel: "Realizada",
    iconKey: "checkCircle",
    tone: "info",
  },
  cancelled_by_patient: {
    label: "Cancelada pelo paciente",
    shortLabel: "Cancelada",
    iconKey: "cancel",
    tone: "error",
    emphasized: true,
  },
  payment_not_completed: {
    label: "Pagamento não realizado",
    shortLabel: "Sem pagamento",
    iconKey: "block",
    tone: "error",
    emphasized: true,
  },
};

export function resolveAppointmentSituationKind(
  appointment: fhir5.Appointment
): AppointmentSituationKind {
  if (appointment.status === "cancelled") return "cancelled_by_patient";
  if (appointment.status === "fulfilled") return "fulfilled";
  if (appointment.status === "pending") {
    return isExpiredPendingAppointment(appointment)
      ? "payment_not_completed"
      : "awaiting_payment";
  }
  if (appointment.status === "booked") {
    return isOverdueBookedAppointment(appointment)
      ? "needs_reschedule"
      : "confirmed";
  }
  return "confirmed";
}

export function getAppointmentSituationMeta(appointment: fhir5.Appointment) {
  return APPOINTMENT_SITUATION_META[resolveAppointmentSituationKind(appointment)];
}

export function hasMultipleAppointmentSituations(
  appointments: fhir5.Appointment[]
): boolean {
  if (appointments.length <= 1) return false;
  const kinds = new Set(appointments.map(resolveAppointmentSituationKind));
  return kinds.size > 1;
}

export type AppointmentSubCaseFilter =
  | "all"
  | "confirmed"
  | "awaiting_payment"
  | "cancelled_by_patient"
  | "payment_not_completed";

export interface SubCaseFilterOption<T extends string> {
  value: T;
  label: string;
}

export function getAppointmentSubCaseFilterOptions(
  status: string[]
): SubCaseFilterOption<AppointmentSubCaseFilter>[] | null {
  if (status.includes("marcadas")) {
    return [
      { value: "all", label: "Todas as situações" },
      { value: "confirmed", label: "Confirmadas (pagas ou gratuitas)" },
      { value: "awaiting_payment", label: "Aguardando pagamento" },
    ];
  }

  if (status.includes("closed")) {
    return [
      { value: "all", label: "Todas as situações" },
      { value: "cancelled_by_patient", label: "Canceladas pelo paciente" },
      { value: "payment_not_completed", label: "Pagamento não realizado" },
    ];
  }

  return null;
}

export function matchesAppointmentSubCaseFilter(
  appointment: fhir5.Appointment,
  filter: AppointmentSubCaseFilter
): boolean {
  if (filter === "all") return true;

  if (filter === "confirmed") {
    return (
      appointment.status === "booked" &&
      !isOverdueBookedAppointment(appointment)
    );
  }

  if (filter === "awaiting_payment") {
    return (
      appointment.status === "pending" &&
      isActionablePendingAppointment(appointment)
    );
  }

  if (filter === "cancelled_by_patient") {
    return appointment.status === "cancelled";
  }

  if (filter === "payment_not_completed") {
    return (
      appointment.status === "pending" &&
      isExpiredPendingAppointment(appointment)
    );
  }

  return true;
}

const APPOINTMENT_CRM_SITUATION_KEYS = new Set([
  "fulfilled",
  "booked",
  "overdueBooked",
  "cancelled",
]);

export function isAppointmentCrmSituationEnabled(status: string[]): boolean {
  if (status.includes("pending")) return false;
  if (status.includes("overdueBooked")) return true;
  if (status.length !== 1) return false;
  return APPOINTMENT_CRM_SITUATION_KEYS.has(status[0]);
}
